import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, IconButton, Alert, TablePagination, Tooltip, Stack,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Divider, Chip, InputAdornment
} from '@mui/material';
import {
  Search, Print, Payment, Mail, Person, Phone, LocationOn,
  CalendarToday, Visibility, FilterList, AssignmentLate,
  BadgeOutlined, ReceiptLong, EventNote, AccountBalanceWallet,
  MoneyOff, NoteAlt, CheckCircleOutline, Close, Edit, Delete,
  HourglassEmpty, CheckCircle, Cancel, History
} from '@mui/icons-material';
import {
  loadDebtAccount,
  loadDebtService,
  saveWriteOff,      // POST   – create new write-off request
  listWriteOff,      // POST   – fetch all write-off records for a patient  { patient_id }
  updateWriteOff,    // PUT    – edit a pending write-off request           { id, writeoff_amount, reason, notes, updated_by }
  deleteWriteOff,    // DELETE – soft-delete a pending write-off request    { id, deleted_by }
} from '../BaseURL';
import Swal from 'sweetalert2';
import axios from 'axios';
import { formatCurrency, formatWithOutSom } from '../Message';
import SimplePaymentAmountDialog from './SimplePaymentAmountDialog';
import ThermalReceipt from './ThermalReceipt';
import PaymentDialog from './PaymentDialog';

/*
 ┌──────────────────────────────────────────────────────────────────────────────┐
 │  Add these four lines to your  ../BaseURL  file:                             │
 │                                                                              │
 │  export const saveWriteOff   = BASE_URL + '/writeoff/save';                 │
 │  export const listWriteOff   = BASE_URL + '/writeoff/list';                 │
 │  export const updateWriteOff = BASE_URL + '/writeoff/update';               │
 │  export const deleteWriteOff = BASE_URL + '/writeoff/delete';               │
 └──────────────────────────────────────────────────────────────────────────────┘
*/

// ─── Alias so the rest of the file uses a tidy object ────────────────────────
const WRITEOFF_URLS = {
  save:   saveWriteOff,
  list:   listWriteOff,
  update: updateWriteOff,
  delete: deleteWriteOff,
};

// ─── Write-off reason options ─────────────────────────────────────────────────
const WRITEOFF_REASONS = [
  { value: 'charity',               label: 'Charity / Indigent Patient' },
  { value: 'nhis_rejection',        label: 'NHIS Rejection' },
  { value: 'billing_error',         label: 'Billing Error' },
  { value: 'duplicate_charge',      label: 'Duplicate Charge' },
  { value: 'professional_courtesy', label: 'Professional Courtesy' },
  { value: 'management_approval',   label: 'Management Approval' },
  { value: 'uncollectible_balance', label: 'Uncollectible Small Balance' },
  { value: 'other',                 label: 'Other' },
];
const reasonLabel = (val) => WRITEOFF_REASONS.find((r) => r.value === val)?.label || val;

// ─── Status chip ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    pending:  { label: 'Pending',  color: '#b45309', bg: '#fef3c7', Icon: HourglassEmpty },
    approved: { label: 'Approved', color: '#065f46', bg: '#d1fae5', Icon: CheckCircle    },
    rejected: { label: 'Rejected', color: '#991b1b', bg: '#fee2e2', Icon: Cancel         },
  };
  const s = map[status] || map.pending;
  return (
    <Chip
      icon={<s.Icon sx={{ fontSize: '15px !important', color: `${s.color} !important` }} />}
      label={s.label} size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 13, border: `1px solid ${s.color}33` }}
    />
  );
};

// ─── Loading Spinner ──────────────────────────────────────────────────────────
const LoadingSpinner = ({ message = 'Loading...', fullScreen = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
    height: fullScreen ? '100vh' : '100%', minHeight: fullScreen ? 'auto' : '200px',
    flexDirection: 'column', gap: 2 }}>
    <CircularProgress size={40} sx={{ color: '#1976d2' }} />
    {message && <Typography sx={{ color: '#666', fontWeight: 500 }}>{message}</Typography>}
  </Box>
);

// ─── InfoRow (write-off form) ─────────────────────────────────────────────────
const InfoRow = ({ icon, label, value, highlight }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.6, px: 2.5,
    bgcolor: highlight ? 'rgba(6,95,70,0.06)' : 'transparent' }}>
    <Box sx={{ color: highlight ? '#065f46' : '#6ee7b7', mt: '3px', flexShrink: 0,
      p: 0.8, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: highlight ? 'rgba(6,95,70,0.1)' : 'rgba(110,231,183,0.12)' }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption"
        sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="body2"
        sx={{ color: highlight ? '#065f46' : '#1f2937', fontWeight: highlight ? 800 : 500,
          wordBreak: 'break-word', mt: 0.2, fontSize: '0.875rem' }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  </Box>
);

// ─── Write-off History Table ──────────────────────────────────────────────────
const WriteOffHistoryTable = ({ records, loading, onEdit, onDelete }) => {
  if (loading)
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: '#065f46' }} /></Box>;

  if (!records.length)
    return (
      <Alert severity="info" icon={<History />}
        sx={{ bgcolor: 'rgba(6,95,70,0.06)', color: '#065f46', border: '1px solid #6ee7b7', fontSize: 13 }}>
        No write-off records found for this patient.
      </Alert>
    );

  return (
    <TableContainer component={Paper} variant="outlined"
      sx={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #6ee7b7', borderRadius: 2 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {['Date', 'Amount', 'Reason', 'Notes', 'Status', 'Requested By', 'Actions'].map((h) => (
              <TableCell key={h}
                sx={{ bgcolor: '#065f46', color: '#fff', fontWeight: 700, fontSize: 15, py: 2,
                  '&.MuiTableCell-head': { bgcolor: '#065f46' } }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((r, i) => (
            <TableRow key={r.id || i}
              sx={{ '&:nth-of-type(odd)': { bgcolor: '#f0fdf4' }, '&:hover': { bgcolor: '#dcfce7' } }}>
              <TableCell sx={{ fontSize: 15, color: '#374151', whiteSpace: 'nowrap', py: 1.8 }}>
                {r.request_date || r.created_at}
              </TableCell>
              <TableCell sx={{ fontSize: 15, color: '#065f46', fontWeight: 700, py: 1.8 }}>
                {formatCurrency(r.writeoff_amount)}
              </TableCell>
              <TableCell sx={{ fontSize: 15, color: '#374151', maxWidth: 160, py: 1.8 }}>
                <Tooltip title={reasonLabel(r.reason)}>
                  <span style={{ cursor: 'default', display: 'block', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 155, fontSize: 15 }}>
                    {reasonLabel(r.reason)}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontSize: 15, color: '#6b7280', maxWidth: 170, py: 1.8 }}>
                <Tooltip title={r.notes || '—'}>
                  <span style={{ cursor: 'default', display: 'block', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 165, fontSize: 15 }}>
                    {r.notes || '—'}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ py: 1.8 }}><StatusChip status={r.status} /></TableCell>
              <TableCell sx={{ fontSize: 15, color: '#374151', py: 1.8 }}>{r.requested_by}</TableCell>
              <TableCell sx={{ py: 1.8 }}>
                {r.status === 'pending' ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit request">
                      <IconButton size="small" onClick={() => onEdit(r)}
                        sx={{ color: '#1976d2', '&:hover': { bgcolor: 'rgba(25,118,210,0.08)' } }}>
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete request">
                      <IconButton size="small" onClick={() => onDelete(r)}
                        sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.08)' } }}>
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>—</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─── Write-off Request Dialog ─────────────────────────────────────────────────
const WriteOffDialog = ({
  open, onClose, debtor, currentUser, onSubmit, submitting,
  writeOffRecords, writeOffRecordsLoading, onEditRecord, onDeleteRecord,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [writeOffAmount, setWriteOffAmount] = useState('');
  const [reason, setReason]               = useState('');
  const [notes, setNotes]                 = useState('');
  const [errors, setErrors]               = useState({});

  useEffect(() => {
    if (open) { setWriteOffAmount(''); setReason(''); setNotes(''); setErrors({}); }
  }, [open, debtor?.id]);

  const balanceDue    = parseFloat(debtor?.balanceDue || 0);
  const enteredAmount = parseFloat(writeOffAmount) || 0;
  const remaining     = balanceDue - enteredAmount;
  const invoiceNumber = debtor
    ? `INV-${debtor.id}-${new Date(debtor.createdAt || Date.now()).getFullYear()}` : '';

  const validate = () => {
    const ne = {};
    const a = parseFloat(writeOffAmount);
    if (!writeOffAmount || isNaN(a) || a <= 0)
      ne.writeOffAmount = 'Please enter a valid write-off amount.';
    else if (a > balanceDue)
      ne.writeOffAmount = `Maximum write-off is ${formatCurrency(balanceDue)} (outstanding balance).`;
    if (!reason) ne.reason = 'Please select a reason for the write-off.';
    setErrors(ne);
    return Object.keys(ne).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({ patientId: debtor?.id, patientName: debtor?.name, invoiceNumber,
      totalBill: debtor?.totalBill, outstandingAmount: debtor?.balanceDue,
      writeOffAmount: parseFloat(writeOffAmount), reason, notes,
      requestedBy: currentUser, requestDate: today });
  };

  const pendingCount = (writeOffRecords || []).filter((r) => r.status === 'pending').length;
  const totalRequested = (writeOffRecords || []).reduce((s, r) => s + parseFloat(r.writeoff_amount || 0), 0);
  const totalApproved  = (writeOffRecords || []).filter((r) => r.status === 'approved').reduce((s, r) => s + parseFloat(r.writeoff_amount || 0), 0);
  const totalPending   = (writeOffRecords || []).filter((r) => r.status === 'pending').reduce((s, r) => s + parseFloat(r.writeoff_amount || 0), 0);

  return (
    <Dialog open={open} onClose={!submitting ? onClose : undefined}
      maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%)',
        px: 3, pt: 3, pb: 2.5, position: 'relative', borderBottom: '3px solid #6ee7b7' }}>
        <IconButton onClick={onClose} disabled={submitting} size="small"
          sx={{ position: 'absolute', top: 10, right: 10, color: '#6ee7b7',
            bgcolor: 'rgba(110,231,183,0.1)', '&:hover': { color: '#fff', bgcolor: 'rgba(110,231,183,0.2)' } }}>
          <Close fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 4 }}>
          <Avatar sx={{ bgcolor: 'rgba(110,231,183,0.18)', width: 52, height: 52, border: '2px solid #6ee7b7', flexShrink: 0 }}>
            <MoneyOff sx={{ color: '#6ee7b7', fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2, letterSpacing: 0.3, fontSize: '1.1rem' }}>
              Request Account Write-off
            </Typography>
            <Typography variant="body2" sx={{ color: '#6ee7b7', mt: 0.3, fontWeight: 500 }}>
              Submit a write-off request for review and approval
            </Typography>
          </Box>
        </Box>
        {debtor && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip icon={<Person sx={{ color: '#065f46 !important', fontSize: 14 }} />}
              label={debtor.name} size="small"
              sx={{ bgcolor: '#6ee7b7', color: '#065f46', fontWeight: 700, fontSize: 11 }} />
            <Chip icon={<BadgeOutlined sx={{ color: '#6ee7b7 !important', fontSize: 13 }} />}
              label={debtor.id} size="small"
              sx={{ bgcolor: 'rgba(110,231,183,0.15)', color: '#6ee7b7', fontWeight: 600, fontSize: 11, border: '1px solid rgba(110,231,183,0.4)' }} />
            {debtor.balanceDue && (
              <Chip icon={<AccountBalanceWallet sx={{ color: '#6ee7b7 !important', fontSize: 13 }} />}
                label={`Balance: ${formatCurrency(debtor.balanceDue)}`} size="small"
                sx={{ bgcolor: 'rgba(110,231,183,0.15)', color: '#6ee7b7', fontWeight: 600, fontSize: 11, border: '1px solid rgba(110,231,183,0.4)' }} />
            )}
            {pendingCount > 0 && (
              <Chip icon={<HourglassEmpty sx={{ color: '#b45309 !important', fontSize: 13 }} />}
                label={`${pendingCount} Pending`} size="small"
                sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700, fontSize: 11, border: '1px solid #fcd34d' }} />
            )}
          </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: '#fafafa' }}>
        {/* Two-column layout */}
        <Box sx={{ display: 'flex', minHeight: 520 }}>

          {/* ── LEFT: Form ── */}
          <Box sx={{ flex: '0 0 44%', borderRight: '2px solid #d1fae5', overflowY: 'auto' }}>
            {/* Patient Info */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              <Typography variant="overline" sx={{ color: '#065f46', fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
                Patient &amp; Bill Information
              </Typography>
            </Box>
            <Box sx={{ mx: 3, mb: 2, borderRadius: 2, border: '1px solid #6ee7b7', bgcolor: '#fff', overflow: 'hidden' }}>
              <Grid container>
                <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
                  <InfoRow icon={<BadgeOutlined sx={{ fontSize: 17 }} />} label="Patient ID" value={debtor?.id} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<Person sx={{ fontSize: 17 }} />} label="Patient Name" value={debtor?.name} />
                </Grid>
                <Grid item xs={12}><Divider sx={{ borderColor: '#d1fae5' }} /></Grid>
                <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
                  <InfoRow icon={<ReceiptLong sx={{ fontSize: 17 }} />} label="Invoice Number" value={invoiceNumber} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<EventNote sx={{ fontSize: 17 }} />} label="Account Date" value={debtor?.createdAt} />
                </Grid>
                <Grid item xs={12}><Divider sx={{ borderColor: '#d1fae5' }} /></Grid>
                <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
                  <InfoRow icon={<Person sx={{ fontSize: 17 }} />} label="Requested By" value={currentUser || 'System'} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<CalendarToday sx={{ fontSize: 17 }} />} label="Request Date" value={today} />
                </Grid>
                <Grid item xs={12}><Divider sx={{ borderColor: '#d1fae5' }} /></Grid>
                <Grid item xs={6} sx={{ borderRight: '1px solid #6ee7b7', bgcolor: 'rgba(6,95,70,0.04)' }}>
                  <InfoRow icon={<AccountBalanceWallet sx={{ fontSize: 17 }} />} label="Total Bill Amount" value={formatCurrency(debtor?.totalBill || 0)} />
                </Grid>
                <Grid item xs={6} sx={{ bgcolor: 'rgba(6,95,70,0.04)' }}>
                  <InfoRow icon={<MoneyOff sx={{ fontSize: 17 }} />} label="Outstanding Amount" value={formatCurrency(debtor?.balanceDue || 0)} highlight />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mx: 3, borderColor: '#d1fae5' }} />

            {/* Write-off Details */}
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
              <Typography variant="overline" sx={{ color: '#065f46', fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
                Write-off Details
              </Typography>
            </Box>
            <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Write-off Amount" type="number" required fullWidth
                value={writeOffAmount}
                onChange={(e) => {
                  const raw = e.target.value;
                  const parsed = parseFloat(raw);
                  if (!isNaN(parsed) && parsed > balanceDue) {
                    setWriteOffAmount(String(balanceDue));
                    setErrors((p) => ({ ...p, writeOffAmount: `Maximum write-off is ${formatCurrency(balanceDue)} (outstanding balance).` }));
                  } else {
                    setWriteOffAmount(raw);
                    if (errors.writeOffAmount && !isNaN(parsed) && parsed > 0 && parsed <= balanceDue)
                      setErrors((p) => ({ ...p, writeOffAmount: '' }));
                  }
                }}
                error={!!errors.writeOffAmount}
                helperText={errors.writeOffAmount || (enteredAmount > 0 && enteredAmount <= balanceDue
                  ? `Remaining balance after write-off: ${formatCurrency(Math.max(0, remaining))}`
                  : `Outstanding balance: ${formatCurrency(balanceDue)}`)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MoneyOff sx={{ color: errors.writeOffAmount ? '#c62828' : '#065f46', fontSize: 20 }} /></InputAdornment>,
                  endAdornment: enteredAmount > 0 && enteredAmount <= balanceDue ? (
                    <InputAdornment position="end">
                      <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 700, whiteSpace: 'nowrap' }}>/ {formatCurrency(balanceDue)}</Typography>
                    </InputAdornment>
                  ) : null,
                }}
                inputProps={{ min: 0.01, max: balanceDue, step: '0.01' }}
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#065f46' } }, '& .MuiInputLabel-root.Mui-focused': { color: '#065f46' } }}
              />

              <FormControl fullWidth required error={!!errors.reason}>
                <InputLabel>Reason for Write-off</InputLabel>
                <Select value={reason} label="Reason for Write-off"
                  onChange={(e) => { setReason(e.target.value); if (errors.reason) setErrors((p) => ({ ...p, reason: '' })); }}
                  startAdornment={<InputAdornment position="start"><AssignmentLate sx={{ color: errors.reason ? '#c62828' : '#065f46', fontSize: 20, ml: 0.5 }} /></InputAdornment>}
                  sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#065f46' } }}>
                  {WRITEOFF_REASONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
                {errors.reason && <Typography variant="caption" sx={{ color: '#c62828', mt: 0.5, ml: 1.5 }}>{errors.reason}</Typography>}
              </FormControl>

              <TextField
                label="Description / Notes" multiline rows={3} fullWidth value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional context or supporting information…"
                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><NoteAlt sx={{ color: '#065f46', fontSize: 20 }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#065f46' } }, '& .MuiInputLabel-root.Mui-focused': { color: '#065f46' } }}
              />

              {enteredAmount > 0 && reason && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                  borderRadius: 2, bgcolor: 'rgba(6,95,70,0.07)', border: '1px dashed #6ee7b7' }}>
                  <CheckCircleOutline sx={{ color: '#065f46', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 500 }}>
                    Requesting write-off of <strong>{formatCurrency(enteredAmount)}</strong> — {reasonLabel(reason)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* ── RIGHT: History ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#f8fffe' }}>
            {/* History header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid #d1fae5',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History sx={{ color: '#065f46', fontSize: 20 }} />
                <Typography variant="overline" sx={{ color: '#065f46', fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
                  Write-off History
                </Typography>
              </Box>
              {writeOffRecords.length > 0 && (
                <Chip label={`${writeOffRecords.length} record${writeOffRecords.length !== 1 ? 's' : ''}`}
                  size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: 10 }} />
              )}
            </Box>

            <Box sx={{ p: 2.5 }}>
              <WriteOffHistoryTable
                records={writeOffRecords}
                loading={writeOffRecordsLoading}
                onEdit={onEditRecord}
                onDelete={onDeleteRecord}
              />

              {/* Summary totals strip */}
              {writeOffRecords.length > 0 && (
                <Box sx={{ mt: 2, p: 0, borderRadius: 2, bgcolor: '#fff', border: '1px solid #6ee7b7',
                  display: 'flex', overflow: 'hidden' }}>
                  {[
                    { label: 'Total Requested', val: totalRequested, color: '#065f46', bg: 'transparent' },
                    { label: 'Approved',         val: totalApproved,  color: '#047857', bg: 'rgba(6,95,70,0.04)' },
                    { label: 'Pending',          val: totalPending,   color: '#b45309', bg: 'rgba(180,83,9,0.04)' },
                  ].map(({ label, val, color, bg }, i, arr) => (
                    <Box key={label} sx={{ flex: 1, textAlign: 'center', py: 1.5, px: 1, bgcolor: bg,
                      borderRight: i < arr.length - 1 ? '1px solid #d1fae5' : 'none' }}>
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5, display: 'block' }}>
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ color, fontWeight: 800, fontSize: 16, mt: 0.3 }}>
                        {formatCurrency(val)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f0fdf4', borderTop: '2px solid #6ee7b7', gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined"
          sx={{ borderColor: '#6ee7b7', color: '#065f46', '&:hover': { bgcolor: 'rgba(110,231,183,0.1)', borderColor: '#065f46' } }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting} variant="contained"
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AssignmentLate />}
          sx={{ background: 'linear-gradient(135deg, #064e3b, #065f46)', fontWeight: 700, px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #022c22, #064e3b)' },
            '&:disabled': { bgcolor: '#e0e0e0', color: '#bdbdbd' } }}>
          {submitting ? 'Submitting…' : 'Submit Write-off Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Edit Write-off Dialog ────────────────────────────────────────────────────
const EditWriteOffDialog = ({ open, onClose, record, debtor, currentUser, onSave, saving }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes]   = useState('');
  const [errors, setErrors] = useState({});

  const balanceDue = parseFloat(debtor?.balanceDue || 0);

  useEffect(() => {
    if (open && record) {
      setAmount(String(record.writeoff_amount || ''));
      setReason(record.reason || '');
      setNotes(record.notes || '');
      setErrors({});
    }
  }, [open, record]);

  const validate = () => {
    const ne = {};
    const a = parseFloat(amount);
    if (!amount || isNaN(a) || a <= 0) ne.amount = 'Enter a valid amount.';
    else if (a > balanceDue)            ne.amount = `Cannot exceed ${formatCurrency(balanceDue)}.`;
    if (!reason)                         ne.reason = 'Please select a reason.';
    setErrors(ne);
    return Object.keys(ne).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ ...record, writeoff_amount: parseFloat(amount), reason, notes });
  };

  return (
    <Dialog open={open} onClose={!saving ? onClose : undefined} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1976d2', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Edit sx={{ color: '#fff', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>Edit Write-off Request</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Only pending requests can be edited</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"
          sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField label="Write-off Amount" type="number" required fullWidth value={amount}
          error={!!errors.amount} helperText={errors.amount || `Max: ${formatCurrency(balanceDue)}`}
          onChange={(e) => { setAmount(e.target.value); if (errors.amount) setErrors((p) => ({ ...p, amount: '' })); }}
          inputProps={{ min: 0.01, max: balanceDue, step: '0.01' }} />
        <FormControl fullWidth required error={!!errors.reason}>
          <InputLabel>Reason</InputLabel>
          <Select value={reason} label="Reason"
            onChange={(e) => { setReason(e.target.value); if (errors.reason) setErrors((p) => ({ ...p, reason: '' })); }}>
            {WRITEOFF_REASONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
          {errors.reason && <Typography variant="caption" sx={{ color: '#c62828', ml: 1.5, mt: 0.5 }}>{errors.reason}</Typography>}
        </FormControl>
        <TextField label="Notes" multiline rows={3} fullWidth value={notes} onChange={(e) => setNotes(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ borderColor: '#bdbdbd', color: '#555' }}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Edit />}
          sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Debtor = () => {
  const [searchTerm, setSearchTerm]               = useState('');
  const [selectedDebtor, setSelectedDebtor]       = useState(null);
  const [dialogOpen, setDialogOpen]               = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [page, setPage]                           = useState(0);
  const [rowsPerPage, setRowsPerPage]             = useState(10);
  const [selectedPeriod, setSelectedPeriod]       = useState('all');
  const [customStartDate, setCustomStartDate]     = useState('');
  const [customEndDate, setCustomEndDate]         = useState('');
  const [servicesStartDate, setServicesStartDate] = useState('');
  const [servicesEndDate, setServicesEndDate]     = useState('');
  const [paymentsStartDate, setPaymentsStartDate] = useState('');
  const [paymentsEndDate, setPaymentsEndDate]     = useState('');
  const [accountsData, setAccountsData]           = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [historyData, setHistoryData]             = useState([]);
  const [paymentData, setPaymentData]             = useState([]);
  const [dialogLoading, setDialogLoading]         = useState(false);
  const [currentUser, setCurrentUser]             = useState('');
  const [simplePaymentDialogOpen, setSimplePaymentDialogOpen] = useState(false);
  const [userEnteredAmount, setUserEnteredAmount] = useState('');
  const [receiptDialog, setReceiptDialog]         = useState({ open: false, receiptData: null });
  const [printLoadingId, setPrintLoadingId]       = useState(null);

  // Write-off state
  const [writeOffDialogOpen, setWriteOffDialogOpen]             = useState(false);
  const [writeOffDebtor, setWriteOffDebtor]                     = useState(null);
  const [writeOffSubmitting, setWriteOffSubmitting]             = useState(false);
  const [writeOffRecords, setWriteOffRecords]                   = useState([]);
  const [writeOffRecordsLoading, setWriteOffRecordsLoading]     = useState(false);
  const [editRecord, setEditRecord]                             = useState(null);
  const [editDialogOpen, setEditDialogOpen]                     = useState(false);
  const [editSaving, setEditSaving]                             = useState(false);

  useEffect(() => {
    loadDebt();
    const saved = localStorage.getItem('userInfo');
    if (saved) { const info = JSON.parse(saved); setCurrentUser(info.name || ''); }
  }, []);

  const loadDebt = async () => {
    try {
      setLoading(true);
      const res = await axios.get(loadDebtAccount);
      if (res.data.success) setAccountsData(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to Load Data', icon: 'error' });
    } finally { setLoading(false); }
  };

  const loadWriteOffRecords = useCallback(async (patientId) => {
    if (!patientId) return;
    setWriteOffRecordsLoading(true);
    try {
      const res = await axios.post(WRITEOFF_URLS.list, { patient_id: patientId });
      setWriteOffRecords(res.data.success ? (res.data.results || []) : []);
    } catch { setWriteOffRecords([]); }
    finally { setWriteOffRecordsLoading(false); }
  }, []);

  const getPeriodDateRange = () => {
    const y = new Date().getFullYear();
    switch (selectedPeriod) {
      case 'first_period':  return { start: new Date(y, 0, 1),  end: new Date(y, 5, 30) };
      case 'second_period': return { start: new Date(y, 6, 1),  end: new Date(y, 11, 31) };
      case 'custom':        return { start: customStartDate ? new Date(customStartDate) : null, end: customEndDate ? new Date(customEndDate) : null };
      default:              return { start: null, end: null };
    }
  };

  const filteredDebtors = useMemo(() => {
    if (!accountsData.length) return [];
    let f = accountsData;
    if (selectedPeriod !== 'all') {
      const { start, end } = getPeriodDateRange();
      if (start || end) f = f.filter((d) => {
        if (!d.createdAt) return false;
        const dt = new Date(d.createdAt);
        if (start && dt < start) return false;
        if (end && dt > end) return false;
        return true;
      });
    }
    if (!searchTerm.trim()) return f;
    f = f.filter((d) => {
      if (!d) return false;
      const sl = searchTerm.toLowerCase();
      return (d.name||'').toLowerCase().includes(sl) || (d.id||'').toLowerCase().includes(sl) || (d.tel||'').includes(searchTerm);
    });
    f.sort((a, b) => {
      const sl = searchTerm.toLowerCase(), stc = searchTerm.replace(/\s+/g, '');
      const score = (d) => {
        const name = (d.name||'').toLowerCase(), id = (d.id||'').toLowerCase(), tel = (d.tel||'').replace(/\s+/g,'');
        let s = 0;
        if(name===sl) s+=1000; if(id===sl) s+=1000; if(tel===searchTerm||tel===stc) s+=1000;
        if(!isNaN(searchTerm)&&searchTerm.length>0){const nm=id.match(/\d+$/);if(nm){const nv=nm[0];if(nv===searchTerm)s+=2000;else if(nv.startsWith(searchTerm))s+=700;else if(nv.includes(searchTerm))s+=500;}if(id.endsWith('_'+searchTerm)||id.endsWith('-'+searchTerm))s+=1500;}
        if(name.startsWith(sl))s+=500;if(id.startsWith(sl))s+=500;if(tel.startsWith(searchTerm)||tel.startsWith(stc))s+=500;
        if(!isNaN(searchTerm)&&searchTerm.length>2){const pd=tel.replace(/\D/g,''),sd=searchTerm.replace(/\D/g,'');if(pd.includes(sd)){s+=pd.indexOf(sd)===0?300:200;s+=sd.length*10;}}
        name.split(/\s+/).forEach(w=>{if(w.startsWith(sl))s+=300;if(w===sl)s+=400;});
        if(name.includes(sl))s+=100;if(id.includes(sl))s+=100;if(tel.includes(searchTerm))s+=100;
        return s;
      };
      const sa=score(a),sb=score(b);
      return sa!==sb ? sb-sa : (a.name||'').toLowerCase().localeCompare((b.name||'').toLowerCase());
    });
    return f;
  }, [searchTerm, accountsData, selectedPeriod, customStartDate, customEndDate]);

  const filterSvc = (s) => {
    if (!servicesStartDate && !servicesEndDate) return s;
    return s.filter((x) => { const d=new Date(x.date); if(servicesStartDate&&d<new Date(servicesStartDate))return false; if(servicesEndDate&&d>new Date(servicesEndDate))return false; return true; });
  };
  const filterPmt = (p) => {
    if (!paymentsStartDate && !paymentsEndDate) return p;
    return p.filter((x) => { const d=new Date(x.date); if(paymentsStartDate&&d<new Date(paymentsStartDate))return false; if(paymentsEndDate&&d>new Date(paymentsEndDate))return false; return true; });
  };

  const clearServicesFilter  = () => { setServicesStartDate('');  setServicesEndDate(''); };
  const clearPaymentsFilter  = () => { setPaymentsStartDate(''); setPaymentsEndDate(''); };
  const clearPeriodFilter    = () => { setSelectedPeriod('all'); setCustomStartDate(''); setCustomEndDate(''); };
  const handleSearch         = () => setPage(0);

  const handlePreviewAndPrint = () => {
    const pw = window.open('', '_blank', 'width=1200,height=800');
    if (!pw) { Swal.fire({ title:'Error', text:'Unable to open print window.', icon:'error' }); return; }
    const tBal=filteredDebtors.reduce((s,d)=>s+parseFloat(d.balanceDue||0),0);
    const tPaid=filteredDebtors.reduce((s,d)=>s+parseFloat(d.cur_paid||0),0);
    const tBill=filteredDebtors.reduce((s,d)=>s+parseFloat(d.totalBill||0),0);
    let pd='All Periods';
    if(selectedPeriod==='first_period')pd='1st Period (January – June)';
    else if(selectedPeriod==='second_period')pd='2nd Period (July – December)';
    else if(selectedPeriod==='custom'&&(customStartDate||customEndDate))pd=`Custom Range: ${customStartDate||'Start'} to ${customEndDate||'End'}`;
    const rows=filteredDebtors.map((d,i)=>`<tr><td style="padding:8px;border:1px solid #ddd;text-align:center;">${i+1}</td><td style="padding:8px;border:1px solid #ddd;">${d.id||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;">${d.name||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;">${d.tel||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;">${d.location||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;text-align:center;">${d.createdAt||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;text-align:right;">${formatWithOutSom(d.cur_paid)}</td><td style="padding:8px;border:1px solid #ddd;text-align:center;">${d.cur_dat||'N/A'}</td><td style="padding:8px;border:1px solid #ddd;text-align:right;color:#f44336;font-weight:bold;">${formatWithOutSom(d.balanceDue)}</td></tr>`).join('');
    pw.document.write(`<!DOCTYPE html><html><head><title>Hospital Debtor Report</title><style>@page{size:A4;margin:15mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;line-height:1.4;color:#333}.header{text-align:center;margin-bottom:20px;padding-bottom:15px;border-bottom:3px solid #1976d2}.header h1{color:#1976d2;font-size:24px;margin-bottom:5px;font-weight:bold}.header h2{color:#666;font-size:16px;font-weight:normal;margin-bottom:8px}.header .meta{color:#888;font-size:10px;margin-top:8px}.sum{display:flex;justify-content:space-between;margin-bottom:20px;padding:15px;background:#f5f5f5;border-radius:4px;border:1px solid #ddd}.sb{text-align:center;flex:1;padding:0 10px}.sb:not(:last-child){border-right:1px solid #ddd}.sl{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:5px;font-weight:600}.sv{font-size:18px;font-weight:bold;color:#1976d2}.sv.b{color:#f44336}.sv.p{color:#4caf50}table{width:100%;border-collapse:collapse;margin-bottom:20px}thead{background:#1976d2;color:white}th{padding:10px 8px;text-align:left;font-weight:600;font-size:10px;border:1px solid #1565c0}tbody tr:nth-child(odd){background:#f9f9f9}td{padding:8px;border:1px solid #ddd;font-size:10px}.tr{background:#e3f2fd!important;font-weight:bold}.tr td{border-top:2px solid #1976d2;padding:12px 8px}.footer{margin-top:30px;padding-top:15px;border-top:2px solid #ddd;text-align:center;font-size:9px;color:#888}.pb{position:fixed;top:20px;right:20px;padding:12px 24px;background:#1976d2;color:white;border:none;border-radius:4px;cursor:pointer;font-size:14px;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,.2);z-index:1000}.pb:hover{background:#1565c0}@media print{.pb{display:none}body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}</style></head><body><button class="pb" onclick="window.print()">🖨️ Print Report</button><div class="header"><h1>Hospital Debtor Report</h1><h2>${pd}</h2><div class="meta">Generated on ${new Date().toLocaleString()} by ${currentUser||'System'}${searchTerm?`<br>Search: "${searchTerm}"`:''}</div></div><div class="sum"><div class="sb"><div class="sl">Total Accounts</div><div class="sv">${filteredDebtors.length}</div></div><div class="sb"><div class="sl">Total Bill</div><div class="sv">${formatCurrency(tBill)}</div></div><div class="sb"><div class="sl">Total Paid</div><div class="sv p">${formatCurrency(tPaid)}</div></div><div class="sb"><div class="sl">Outstanding</div><div class="sv b">${formatCurrency(tBal)}</div></div></div><table><thead><tr><th>#</th><th>Patient ID</th><th>Name</th><th>Contact</th><th>Location</th><th>Account Date</th><th>Last Payment</th><th>Payment Date</th><th>Balance Due</th></tr></thead><tbody>${rows}<tr class="tr"><td colspan="6" style="text-align:right;padding-right:15px;"><strong>TOTALS:</strong></td><td style="text-align:right;"><strong>${formatCurrency(tPaid)}</strong></td><td></td><td style="text-align:right;color:#f44336;"><strong>${formatCurrency(tBal)}</strong></td></tr></tbody></table><div class="footer"><p>Hospital Debtor Management System</p><p>Computer-generated report. Contact finance for discrepancies.</p></div></body></html>`);
    pw.document.close();
  };

  const handleViewDetails = async (debtor) => {
    setSelectedDebtor(debtor); setDialogOpen(true); setDialogLoading(true);
    setHistoryData([]); setPaymentData([]);
    try {
      const res = await axios.post(loadDebtService, { id: debtor.id });
      if (res.data.success) { setHistoryData(res.data.results); setPaymentData(res.data.payment); }
      else await Swal.fire({ title:'Error', text: res.data.message||'Failed to load patient details', icon:'error' });
    } catch (err) {
      await Swal.fire({ title:'Error', text: err.response?.data?.message||'Failed to Load Data', icon:'error' });
      setDialogOpen(false); setSelectedDebtor(null);
    } finally { setDialogLoading(false); }
  };

  const handleRecordPayment = (debtor) => { setSelectedDebtor(debtor); setSimplePaymentDialogOpen(true); setUserEnteredAmount(''); };

  const handlePrintReceipt = async (debtor) => {
    try {
      setPrintLoadingId(debtor.id);
      let svc=historyData, pmt=paymentData;
      if (!dialogOpen||historyData.length===0) {
        const res=await axios.post(loadDebtService,{id:debtor.id});
        if(res.data.success){svc=res.data.results;pmt=res.data.payment;}
      }
      setReceiptDialog({ open:true, receiptData:{ patientId:debtor.id, patientName:debtor.name,
        receipt:`DEBT-${debtor.id}-${new Date().getTime()}`, time:new Date().toLocaleString(),
        description:'Debt Account Summary', total:Number(Number(debtor.balanceDue)+Number(debtor.cur_paid)),
        paid:Number(debtor.cur_paid||0), debt:Number(debtor.balanceDue||0), balance:Number(debtor.balanceDue||0),
        status:'paid', paymentMode:'N/A', user:currentUser||'System' }});
    } catch { await Swal.fire({title:'Error',text:'Failed to generate receipt',icon:'error'}); }
    finally { setPrintLoadingId(null); }
  };

  // ── Write-off handlers ────────────────────────────────────────────────────
  const handleOpenWriteOff = (debtor) => {
    setWriteOffDebtor(debtor);
    setWriteOffDialogOpen(true);
    loadWriteOffRecords(debtor.id);
  };

  const handleWriteOffSubmit = async (payload) => {
    setWriteOffSubmitting(true);
    try {
      const res = await axios.post(WRITEOFF_URLS.save, {
        patient_id:      payload.patientId,
        patient_name:    payload.patientName,
        invoice_number:  payload.invoiceNumber,
        total_bill:      payload.totalBill,
        outstanding_amt: payload.outstandingAmount,
        writeoff_amount: payload.writeOffAmount,
        reason:          payload.reason,
        notes:           payload.notes,
        requested_by:    payload.requestedBy,
        request_date:    payload.requestDate,
        status:          'pending',
      });
      if (!res.data.success) throw new Error(res.data.message || 'Submission failed');
      await loadWriteOffRecords(payload.patientId);
      await Swal.fire({
        title: 'Request Submitted',
        html: `<p style="color:#555;margin-bottom:8px;">Write-off request for <strong>${payload.patientName}</strong> submitted successfully.</p>
               <p style="color:#888;font-size:13px;">Amount: <strong style="color:#065f46;">${formatCurrency(payload.writeOffAmount)}</strong></p>
               <p style="color:#888;font-size:13px;">It will be reviewed by the finance team.</p>`,
        icon: 'success', confirmButtonColor: '#065f46',
      });
    } catch (err) {
      await Swal.fire({ title:'Submission Failed', text: err.response?.data?.message||err.message||'Please try again.', icon:'error' });
    } finally { setWriteOffSubmitting(false); }
  };

  const handleEditRecord = (record) => { setEditRecord(record); setEditDialogOpen(true); };

  const handleEditSave = async (updated) => {
    setEditSaving(true);
    try {
      const res = await axios.put(WRITEOFF_URLS.update, {
        id: updated.id, writeoff_amount: updated.writeoff_amount,
        reason: updated.reason, notes: updated.notes, updated_by: currentUser,
      });
      if (!res.data.success) throw new Error(res.data.message || 'Update failed');
      setEditDialogOpen(false); setEditRecord(null);
      await loadWriteOffRecords(writeOffDebtor?.id);
      await Swal.fire({ title:'Updated', text:'Write-off request updated successfully.', icon:'success', confirmButtonColor:'#1976d2' });
    } catch (err) {
      await Swal.fire({ title:'Update Failed', text: err.response?.data?.message||err.message, icon:'error' });
    } finally { setEditSaving(false); }
  };

  const handleDeleteRecord = async (record) => {
    const confirm = await Swal.fire({
      title: 'Delete Write-off Request?',
      html: `<p style="color:#555;">Are you sure you want to delete this pending request?</p>
             <p style="color:#dc2626;font-weight:600;margin-top:8px;">Amount: ${formatCurrency(record.writeoff_amount)}</p>
             <p style="color:#888;font-size:13px;">This action cannot be undone.</p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await axios.delete(WRITEOFF_URLS.delete, { data: { id: record.id, deleted_by: currentUser } });
      if (!res.data.success) throw new Error(res.data.message || 'Delete failed');
      await loadWriteOffRecords(writeOffDebtor?.id);
      await Swal.fire({ title:'Deleted', text:'Write-off request deleted.', icon:'success', confirmButtonColor:'#065f46' });
    } catch (err) {
      await Swal.fire({ title:'Delete Failed', text: err.response?.data?.message||err.message, icon:'error' });
    }
  };

  const handleProceedToPaymentDialog = () => {
    if (!userEnteredAmount||parseFloat(userEnteredAmount)<=0) { Swal.fire({title:'Invalid Amount',text:'Please enter a valid payment amount',icon:'warning'}); return; }
    if (parseFloat(userEnteredAmount)>(selectedDebtor?.balanceDue||0)) { Swal.fire({title:'Amount Too High',text:'Payment amount cannot exceed the outstanding balance',icon:'warning'}); return; }
    setSimplePaymentDialogOpen(false); setPaymentDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false); setHistoryData([]); setPaymentData([]); setSelectedDebtor(null);
    setServicesStartDate(''); setServicesEndDate(''); setPaymentsStartDate(''); setPaymentsEndDate('');
  };

  if (loading) return <LoadingSpinner message="Loading debtor accounts..." fullScreen />;

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">Hospital Debtor</Typography>
        <Typography variant="subtitle1" color="text.secondary">Manage patient accounts, track outstanding balances, and process payments</Typography>
      </Box>

      {/* Search & Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth variant="outlined" placeholder="Search by name, ID, or phone..."
                value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)}
                onKeyPress={(e)=>{if(e.key==='Enter')handleSearch();}}
                InputProps={{ startAdornment:<Search sx={{mr:1,color:'text.secondary'}} /> }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Period Filter</InputLabel>
                <Select value={selectedPeriod} label="Period Filter" onChange={(e)=>setSelectedPeriod(e.target.value)}
                  startAdornment={<FilterList sx={{mr:1,color:'text.secondary'}} />}>
                  <MenuItem value="all">All Periods</MenuItem>
                  <MenuItem value="first_period">1st Period (Jan – Jun)</MenuItem>
                  <MenuItem value="second_period">2nd Period (Jul – Dec)</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedPeriod==='custom'&&(<>
              <Grid item xs={12} md={2}><TextField fullWidth type="date" label="Start Date" value={customStartDate} onChange={(e)=>setCustomStartDate(e.target.value)} InputLabelProps={{shrink:true}} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="date" label="End Date" value={customEndDate} onChange={(e)=>setCustomEndDate(e.target.value)} InputLabelProps={{shrink:true}} /></Grid>
            </>)}
            <Grid item xs={12} md={selectedPeriod==='custom'?12:4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{bgcolor:'#1976d2','&:hover':{bgcolor:'#1565c0'}}}>Search</Button>
                {selectedPeriod!=='all'&&<Button variant="outlined" onClick={clearPeriodFilter} sx={{borderColor:'#1976d2',color:'#1976d2'}}>Clear Filter</Button>}
                <Button variant="contained" startIcon={<Print />} onClick={handlePreviewAndPrint} disabled={filteredDebtors.length===0}
                  sx={{bgcolor:'#4caf50','&:hover':{bgcolor:'#45a049'},'&:disabled':{bgcolor:'#e0e0e0',color:'#9e9e9e'}}}>Preview &amp; Print</Button>
              </Stack>
            </Grid>
          </Grid>
          {selectedPeriod!=='all'&&(
            <Box sx={{mt:2,p:2,bgcolor:'#e3f2fd',borderRadius:1,border:'1px solid #90caf9'}}>
              <Typography variant="body2" sx={{color:'#1976d2',fontWeight:500}}>
                <FilterList sx={{fontSize:18,verticalAlign:'middle',mr:1}} />
                Showing results for:{' '}
                {selectedPeriod==='first_period'&&'1st Period (January – June)'}
                {selectedPeriod==='second_period'&&'2nd Period (July – December)'}
                {selectedPeriod==='custom'&&`Custom Range${customStartDate?` from ${customStartDate}`:''}${customEndDate?` to ${customEndDate}`:''}`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Debtors Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary" sx={{mb:2}}>Debtor Accounts ({filteredDebtors.length})</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{bgcolor:'#f5f5f5'}}>
                  {['Patient ID','Name','Contact','Location','Account Date','Last Payment','Balance','Actions'].map(h=>(
                    <TableCell key={h}><strong>{h}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDebtors.slice(page*rowsPerPage,page*rowsPerPage+rowsPerPage).map((debtor)=>(
                  <TableRow key={debtor.id} hover>
                    <TableCell><Box sx={{display:'flex',alignItems:'center'}}><Avatar sx={{mr:2,bgcolor:'primary.main'}}><Person /></Avatar>{debtor.id||'N/A'}</Box></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="medium">{debtor.name||'N/A'}</Typography></TableCell>
                    <TableCell><Box sx={{display:'flex',alignItems:'center'}}><Phone sx={{fontSize:16,mr:1,color:'text.secondary'}} /><Typography variant="body2">{debtor.tel||'N/A'}</Typography></Box></TableCell>
                    <TableCell><Box sx={{display:'flex',alignItems:'center'}}><LocationOn sx={{fontSize:16,mr:1,color:'text.secondary'}} /><Typography variant="body2">{debtor.location||'N/A'}</Typography></Box></TableCell>
                    <TableCell><Box sx={{display:'flex',alignItems:'center'}}><CalendarToday sx={{fontSize:16,mr:1,color:'text.secondary'}} /><Typography variant="body2">{debtor.createdAt||'N/A'}</Typography></Box></TableCell>
                    <TableCell><Box><Typography variant="body2" sx={{fontWeight:600}}>{formatWithOutSom(debtor.cur_paid)}</Typography><Typography variant="caption" color="text.secondary">{debtor.cur_dat}</Typography></Box></TableCell>
                    <TableCell><Typography variant="body2" color="error" fontWeight="bold">{formatWithOutSom(debtor.balanceDue)}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details"><IconButton size="small" onClick={()=>handleViewDetails(debtor)} color="primary"><Visibility /></IconButton></Tooltip>
                        <Tooltip title="Record Payment"><IconButton size="small" onClick={(e)=>{e.stopPropagation();handleRecordPayment(debtor);}} color="success"><Payment /></IconButton></Tooltip>
                        <Tooltip title="Print Receipt"><IconButton size="small" color="info" onClick={()=>handlePrintReceipt(debtor)} disabled={printLoadingId===debtor.id}>{printLoadingId===debtor.id?<CircularProgress size={20}/>:<Print />}</IconButton></Tooltip>
                        <Tooltip title="Send Reminder"><IconButton size="small" color="warning"><Mail /></IconButton></Tooltip>
                        <Tooltip title="Request Write-off">
                          <IconButton size="small" onClick={(e)=>{e.stopPropagation();handleOpenWriteOff(debtor);}}
                            sx={{ color:'#065f46','&:hover':{bgcolor:'rgba(6,95,70,0.08)',color:'#064e3b'} }}>
                            <MoneyOff />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredDebtors.length} page={page}
            onPageChange={(e,n)=>setPage(n)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e)=>setRowsPerPage(parseInt(e.target.value,10))} />
        </CardContent>
      </Card>

      {/* Patient Details Dialog */}
      <Dialog open={dialogOpen} maxWidth="lg" fullWidth>
        <DialogTitle sx={{bgcolor:'#4caf50',color:'white'}}>
          <Box sx={{display:'flex',alignItems:'center'}}>
            <Avatar sx={{mr:2,bgcolor:'rgba(255,255,255,0.2)'}}><Person sx={{color:'white'}} /></Avatar>
            <Box>
              <Typography variant="h6" sx={{fontWeight:600}}>{selectedDebtor?.name||'N/A'}</Typography>
              <Typography variant="body2" sx={{opacity:0.9}}>Patient ID: {selectedDebtor?.id||'N/A'} | Contact: {selectedDebtor?.tel||'N/A'}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{bgcolor:'#f5f5f5'}}>
          {dialogLoading?<LoadingSpinner message="Loading patient details..."/>:selectedDebtor&&(
            <Grid container spacing={3} columns={{xs:4,sm:8,md:12}}>
              <Grid item xs={12} md={6} size={6}>
                <Paper sx={{p:3,height:'100%',bgcolor:'white',border:'1px solid #e0e0e0'}}>
                  <Typography variant="h6" gutterBottom sx={{color:'#333',fontWeight:600,display:'flex',alignItems:'center',mb:3}}><Person sx={{mr:1,color:'#1976d2'}}/>Patient Information</Typography>
                  {[{label:'Date Created',value:selectedDebtor.createdAt},{label:'Location',value:selectedDebtor.location}].map(({label,value})=>(
                    <Box key={label} sx={{mb:1}}><Typography variant="body2" sx={{color:'#666',fontWeight:500,mb:0.5}}>{label}</Typography><Typography variant="body1" sx={{color:'#333',bgcolor:'#f9f9f9',px:2,py:1,borderRadius:'4px',border:'1px solid #e0e0e0'}}>{value||'N/A'}</Typography></Box>
                  ))}
                  <Box sx={{mb:1}}><Typography variant="body2" sx={{color:'#666',fontWeight:500,mb:0.5}}>Total Account Bill</Typography><Typography variant="h5" sx={{color:'#1976d2',fontWeight:600,bgcolor:'#f9f9f9',px:2,py:1,borderRadius:'4px',border:'1px solid #1976d2'}}>{formatCurrency(selectedDebtor.totalBill||0)}</Typography></Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6} size={6}>
                <Paper sx={{p:3,height:'100%',bgcolor:'white',border:'1px solid #e0e0e0'}}>
                  <Typography variant="h6" gutterBottom sx={{color:'#333',fontWeight:600,display:'flex',alignItems:'center',mb:3}}><Payment sx={{mr:1,color:'#1976d2'}}/>Financial Summary</Typography>
                  {[{label:'Amount Paid',value:formatCurrency(selectedDebtor.amountPaid||0),color:'#4caf50'},{label:'Amount Written Off',value:formatCurrency(0),color:'#666'},{label:'Balance Due',value:formatCurrency(selectedDebtor.balanceDue||0),color:'#f44336'}].map(({label,value,color})=>(
                    <Box key={label} sx={{mb:1}}><Typography variant="body2" sx={{color:'#666',fontWeight:500,mb:0.5}}>{label}</Typography><Typography variant="h6" sx={{color,fontWeight:600,bgcolor:'#f9f9f9',px:2,py:1,borderRadius:'4px',border:'1px solid #e0e0e0'}}>{value}</Typography></Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} size={12}>
                <Paper sx={{p:3,bgcolor:'white',border:'1px solid #e0e0e0'}}>
                  <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2}}>
                    <Typography variant="h6" sx={{color:'#333',fontWeight:600,display:'flex',alignItems:'center'}}><Person sx={{mr:1,color:'#1976d2'}}/>Debt History</Typography>
                    <Box sx={{display:'flex',gap:2,alignItems:'center'}}>
                      <TextField label="Start Date" type="date" size="small" value={servicesStartDate} onChange={(e)=>setServicesStartDate(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
                      <TextField label="End Date" type="date" size="small" value={servicesEndDate} onChange={(e)=>setServicesEndDate(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
                      {(servicesStartDate||servicesEndDate)&&<Button size="small" onClick={clearServicesFilter} variant="outlined" sx={{color:'#1976d2',borderColor:'#1976d2'}}>Clear</Button>}
                    </Box>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{maxHeight:230,overflowY:'auto',border:'1px solid #e0e0e0'}}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>{['Service','Date','User','Amount'].map((h,i)=>(<TableCell key={h} align={i===3?'right':'left'} sx={{bgcolor:'#1b741eff',color:'white',fontWeight:600,'&.MuiTableCell-head':{bgcolor:'#1b741eff'}}}>{h}</TableCell>))}</TableRow></TableHead>
                      <TableBody>
                        {filterSvc(historyData).map((s,i)=>(<TableRow key={i} sx={{'&:nth-of-type(odd)':{bgcolor:'#f9f9f9'},'&:hover':{bgcolor:'#f0f0f0'}}}><TableCell>{s.service}</TableCell><TableCell>{s.date}</TableCell><TableCell>{s.user}</TableCell><TableCell align="right" sx={{color:'#4caf50',fontWeight:600}}>{formatWithOutSom(s.amount)}</TableCell></TableRow>))}
                        {filterSvc(historyData).length===0&&<TableRow><TableCell colSpan={4} align="center"><Typography sx={{color:'#666',fontStyle:'italic'}}>No services found for the selected date range</Typography></TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} size={12}>
                <Paper sx={{p:3,bgcolor:'white',border:'1px solid #e0e0e0'}}>
                  <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2}}>
                    <Typography variant="h6" sx={{color:'#333',fontWeight:600,display:'flex',alignItems:'center'}}><Payment sx={{mr:1,color:'#1976d2'}}/>Payment History</Typography>
                    <Box sx={{display:'flex',gap:2,alignItems:'center'}}>
                      <TextField label="Start Date" type="date" size="small" value={paymentsStartDate} onChange={(e)=>setPaymentsStartDate(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
                      <TextField label="End Date" type="date" size="small" value={paymentsEndDate} onChange={(e)=>setPaymentsEndDate(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
                      {(paymentsStartDate||paymentsEndDate)&&<Button size="small" onClick={clearPaymentsFilter} variant="outlined" sx={{color:'#1976d2',borderColor:'#1976d2'}}>Clear</Button>}
                    </Box>
                  </Box>
                  {paymentData.length>0?(
                    <TableContainer component={Paper} variant="outlined" sx={{maxHeight:200,overflowY:'auto',border:'1px solid #e0e0e0'}}>
                      <Table size="small" stickyHeader>
                        <TableHead><TableRow>{['Date','Method','Paid By','Amount'].map((h,i)=>(<TableCell key={h} align={i===3?'right':'left'} sx={{bgcolor:'#4caf50',color:'white',fontWeight:600,'&.MuiTableCell-head':{bgcolor:'#4caf50'}}}>{h}</TableCell>))}</TableRow></TableHead>
                        <TableBody>
                          {filterPmt(paymentData).map((p,i)=>(<TableRow key={i} sx={{'&:nth-of-type(odd)':{bgcolor:'#f9f9f9'},'&:hover':{bgcolor:'#f0f0f0'}}}><TableCell>{p.date}</TableCell><TableCell>{p.method}</TableCell><TableCell>{p.user}</TableCell><TableCell align="right" sx={{color:'#4caf50',fontWeight:600}}>{formatWithOutSom(p.amount)}</TableCell></TableRow>))}
                          {filterPmt(paymentData).length===0&&<TableRow><TableCell colSpan={4} align="center"><Typography sx={{color:'#666',fontStyle:'italic'}}>No payments found for the selected date range</Typography></TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ):(
                    <Alert severity="info" sx={{bgcolor:'#e3f2fd',color:'#1976d2'}}>No payments recorded yet</Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{bgcolor:'#fafafa',borderTop:'1px solid #e0e0e0',p:3,gap:2}}>
          <Button onClick={handleDialogClose} sx={{color:'#666',border:'1px solid #e0e0e0','&:hover':{bgcolor:'#f5f5f5',borderColor:'#bdbdbd'}}}>Close</Button>
          <Button variant="outlined" startIcon={<MoneyOff />} disabled={dialogLoading}
            onClick={()=>{handleDialogClose();handleOpenWriteOff(selectedDebtor);}}
            sx={{borderColor:'#065f46',color:'#065f46','&:hover':{bgcolor:'rgba(6,95,70,0.06)',borderColor:'#064e3b'},'&:disabled':{borderColor:'#e0e0e0',color:'#bdbdbd'}}}>
            Request Write-off
          </Button>
          <Button variant="contained" startIcon={<Payment />} disabled={dialogLoading}
            onClick={()=>{handleDialogClose();handleRecordPayment(selectedDebtor);}}
            sx={{bgcolor:'#1976d2','&:hover':{bgcolor:'#1565c0'},'&:disabled':{bgcolor:'#e0e0e0',color:'#bdbdbd'}}}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment dialogs */}
      <PaymentDialog open={paymentDialogOpen} onClose={()=>setPaymentDialogOpen(false)}
        selectedTotal={parseFloat(userEnteredAmount)||selectedDebtor?.balanceDue||0}
        allowedPaymentMethods={['cash','mobile_money','mobile_money(manual)']}
        isDebtPayment={true} paymentType="debt" defaultPaymentMethod="cash"
        selectedPatient={{fullName:selectedDebtor?.name||'',id:selectedDebtor?.id||'',age:selectedDebtor?.age||'N/A',gender:selectedDebtor?.gender||'N/A',type:selectedDebtor?.type||'Debt',tel:selectedDebtor?.tel||'',res:selectedDebtor?.location||'',a_name:selectedDebtor?.a_name||'',state:selectedDebtor?.state||'Active',reg_date:selectedDebtor?.createdAt||new Date()}}
        selectedCharges={selectedDebtor?[`debt_${selectedDebtor.id}`]:[]} allCharges={[]} currentUser={currentUser}
        serviceCharges={[]} investigationCharges={[]} medicationCharges={[]} otherCharges={[]}
        onPaymentSuccess={()=>{loadDebt();if(dialogOpen&&selectedDebtor)handleViewDetails(selectedDebtor);setUserEnteredAmount('');}} />
      <SimplePaymentAmountDialog open={simplePaymentDialogOpen} onClose={()=>{setSimplePaymentDialogOpen(false);setUserEnteredAmount('');}}
        patient={selectedDebtor} amount={userEnteredAmount} onAmountChange={setUserEnteredAmount}
        onProceed={handleProceedToPaymentDialog} formatCurrency={formatCurrency} />
      <ThermalReceipt open={receiptDialog.open} onClose={()=>setReceiptDialog({open:false,receiptData:null})} receiptData={receiptDialog.receiptData} />

      {/* Write-off Dialog */}
      <WriteOffDialog
        open={writeOffDialogOpen}
        onClose={()=>{ if(!writeOffSubmitting){setWriteOffDialogOpen(false);setWriteOffDebtor(null);setWriteOffRecords([]); } }}
        debtor={writeOffDebtor} currentUser={currentUser}
        onSubmit={handleWriteOffSubmit} submitting={writeOffSubmitting}
        writeOffRecords={writeOffRecords} writeOffRecordsLoading={writeOffRecordsLoading}
        onEditRecord={handleEditRecord} onDeleteRecord={handleDeleteRecord}
      />

      {/* Edit Write-off Dialog */}
      <EditWriteOffDialog
        open={editDialogOpen}
        onClose={()=>{ if(!editSaving){setEditDialogOpen(false);setEditRecord(null);} }}
        record={editRecord} debtor={writeOffDebtor} currentUser={currentUser}
        onSave={handleEditSave} saving={editSaving}
      />
    </Box>
  );
};

export default Debtor;



